/**
 * /hospitality/fnb/performance — Food & Beverage outlet performance.
 *
 * Mirrors an F&B controller's day: covers, food vs beverage sales, check
 * average, and comps for each outlet (restaurant, bar, banquet, etc.), with
 * a property-wide summary on top.
 *
 * Money fields arrive as decimal strings; `money()` formats them. Covers are
 * plain numbers.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import {
  PageHeader,
  StatCard,
  Panel,
  money,
} from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/fnb/performance")({
  component: FnbPerformancePage,
});

// ── Types — matches GET /api/hospitality/fnb/performance contract ──────────
interface FnbOutlet {
  code: string;
  name: string;
  kind: string;
  covers: number;
  food_sales: string;
  beverage_sales: string;
  total_sales: string;
  check_average: string;
  comps: string;
}
interface FnbPerformanceTotals {
  covers: number;
  food_sales: string;
  beverage_sales: string;
  total_sales: string;
  check_average: string;
  comps: string;
}
interface FnbPerformanceResult {
  property: string;
  business_date: string | null;
  by_outlet: FnbOutlet[];
  totals: FnbPerformanceTotals | null;
  message?: string;
}

function FnbPerformancePage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const q = useQuery({
    queryKey: ["hospitality-fnb-performance"],
    queryFn: () => api<FnbPerformanceResult>("/api/hospitality/fnb/performance"),
  });

  const data = q.data;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="F&B Performance"
        subtitle={
          data
            ? `${data.property}${data.business_date ? ` · ${data.business_date}` : ""}`
            : "Covers, sales & check average by outlet"
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {q.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!q.isLoading && data?.message && (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {data.message}
            <br />
            <span className="text-xs">
              F&B performance populates once outlet sales are imported for a business date.
            </span>
          </div>
        )}

        {!q.isLoading && data && !data.message && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
              <StatCard
                label="Covers"
                value={(data.totals?.covers ?? 0).toLocaleString()}
              />
              <StatCard label="Total sales" value={money(data.totals?.total_sales ?? null, 0)} />
              <StatCard label="Check average" value={money(data.totals?.check_average ?? null)} />
              <StatCard label="Food" value={money(data.totals?.food_sales ?? null, 0)} />
              <StatCard label="Beverage" value={money(data.totals?.beverage_sales ?? null, 0)} />
            </div>

            {/* By outlet */}
            <Panel label="By outlet">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Outlet</th>
                    <th className="text-right py-1.5 px-3">Covers</th>
                    <th className="text-right py-1.5 px-3">Food</th>
                    <th className="text-right py-1.5 px-3">Beverage</th>
                    <th className="text-right py-1.5 px-3">Total</th>
                    <th className="text-right py-1.5 px-3">Check avg</th>
                    <th className="text-right py-1.5 pl-3">Comps</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_outlet.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="py-12 text-center text-slate-500 dark:text-slate-400"
                      >
                        No outlet activity for this business date.
                      </td>
                    </tr>
                  )}
                  {data.by_outlet.map((o) => (
                    <tr key={o.code} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1.5 pr-3">
                        <div className="text-slate-800 dark:text-slate-200">{o.name}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">{o.kind}</div>
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {o.covers.toLocaleString()}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {money(o.food_sales)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {money(o.beverage_sales)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(o.total_sales)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-700 dark:text-slate-200">
                        {money(o.check_average)}
                      </td>
                      <td className="py-1.5 pl-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {money(o.comps)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}
